/**
 * cropMergeSelection — чистая логика мультивыбора эталон-первым (AK-271).
 *
 * Инвариант: первый отмеченный элемент = эталон (keeper). Звезда позволяет
 * переназначить эталон на любой уже выбранный элемент. Вынесено из Crops.tsx
 * чтобы покрыть unit-тестами без рендера всей страницы.
 *
 * Состояние:
 *   - `order`: id в порядке отметки (order[0] не обязан быть эталоном —
 *     эталон хранится отдельно, чтобы переназначение звездой не пересортировывало
 *     список визуально).
 *   - `keeperId`: текущий эталон. По умолчанию = первый отмеченный.
 *
 * target (эталон) выживает; sources = все выбранные кроме эталона.
 */

export interface MergeSelection {
  /** id в порядке отметки. */
  order: string[];
  /** Текущий эталон (target). null если ничего не выбрано. */
  keeperId: string | null;
}

export const EMPTY_SELECTION: MergeSelection = { order: [], keeperId: null };

/** Отметить/снять элемент. Первый отмеченный становится эталоном. */
export function toggle(sel: MergeSelection, id: string): MergeSelection {
  if (sel.order.includes(id)) {
    const order = sel.order.filter((x) => x !== id);
    // Если сняли эталон — новый эталон = первый из оставшихся (или null).
    const keeperId =
      sel.keeperId === id ? (order[0] ?? null) : sel.keeperId;
    return { order, keeperId };
  }
  const order = [...sel.order, id];
  // Первый выбранный = эталон по умолчанию.
  const keeperId = sel.keeperId ?? id;
  return { order, keeperId };
}

/** Назначить эталоном уже выбранный элемент (клик по звезде). No-op если не выбран. */
export function setKeeper(sel: MergeSelection, id: string): MergeSelection {
  if (!sel.order.includes(id)) return sel;
  return { ...sel, keeperId: id };
}

/** Полный сброс выбора. */
export function clear(): MergeSelection {
  return EMPTY_SELECTION;
}

/** id источников (всё выбранное, кроме эталона), в порядке отметки. */
export function sourceIds(sel: MergeSelection): string[] {
  return sel.order.filter((id) => id !== sel.keeperId);
}

/** Количество выбранных. */
export function selectedCount(sel: MergeSelection): number {
  return sel.order.length;
}

/**
 * Можно ли объединять: эталон задан И есть ≥1 источник (т.е. выбрано ≥2).
 */
export function canMerge(sel: MergeSelection): boolean {
  return sel.keeperId !== null && sourceIds(sel).length >= 1;
}

export function isSelected(sel: MergeSelection, id: string): boolean {
  return sel.order.includes(id);
}

export function isKeeper(sel: MergeSelection, id: string): boolean {
  return sel.keeperId === id;
}
